import React, { useMemo, useState } from 'react';
import { CopyOutlined, EyeFilled, EyeInvisibleFilled } from '@ant-design/icons';
import { Flex, Space, Typography, Spin, Button } from 'antd';
import { useIntl } from 'react-intl';
import { RoundContainer } from 'shared/ui';
import cardApi, { useLazyGetCardInfoQuery, useLazyGetNumberAndCvnQuery } from 'entities/card/api';
import { useClipboard, useMessage } from 'shared/libs/hooks';
import { AppDispatch } from 'shared/config/store/store';
import { useDispatch } from 'react-redux';

const { Text, Title } = Typography;

type RequisitesProps = {
  numberMasked: string;
  accountId: string;
  cardId: string;
};

type RequisitesType = {
  number: string;
  cvn: string;
  expirationDate: string;
};

const requisitesInitialState: RequisitesType = {
  number: '',
  cvn: '',
  expirationDate: '',
};

const Requisites: React.FC<RequisitesProps> = ({
  numberMasked,
  accountId,
  cardId,
}) => {
  const { formatMessage: t } = useIntl();
  const dispatch = useDispatch<AppDispatch>();

  const [shown, setShown] = useState<boolean>(false);
  const [loading, setLoading] = useState<boolean>(false);

  const { showError, showSuccess } = useMessage();
  const { copyToClipboard } = useClipboard();

  const [getNumberAndCvn, { data: numberAndCvn }] =
    useLazyGetNumberAndCvnQuery();
  const [getCardInfo, { data: info }] = useLazyGetCardInfoQuery();

  const requisites: RequisitesType = useMemo(() => {
    const number = numberAndCvn?.response.number;
    const cvn = numberAndCvn?.response.cvn;
    const expiration_month = info?.response.expirationDate?.slice(5, 7);
    const expiration_year = info?.response.expirationDate?.slice(2, 4);
    const expirationDate =
      expiration_month && expiration_year
        ? expiration_month + ' / ' + expiration_year
        : '';

    return {
      cvn: cvn || '',
      number: number || '',
      expirationDate,
    };
  }, [
    numberAndCvn?.response.number,
    numberAndCvn?.response.cvn,
    info?.response.expirationDate,
  ]);

  const dataToShow = shown ? requisites : requisitesInitialState;

  const onShow = async () => {
    try {
      setLoading(true);
      await getNumberAndCvn({ accountId, cardId });
      await getCardInfo({ accountId, cardId });
      setShown(true);
    } catch (err) {
      showError(t({ id: 'errors.errorOccurred' }));
    } finally {
      setLoading(false);
    }
  };

  const onHide = () => {
    setShown(false);
  };

  const onToggle = () => (shown ? onHide() : onShow());

  const onCopyCvn = async () => {
    try {
      setLoading(true);

      const { data: numberAndCvn } = await dispatch(
        cardApi.endpoints.getNumberAndCvn.initiate({
          accountId,
          cardId,
        })
      );
      const cvn = numberAndCvn ? numberAndCvn.response.cvn : undefined;
      if (!cvn) throw new Error();

      copyToClipboard(cvn);
      showSuccess(t({ id: 'toasts.copied' }));
    } catch (err) {
      showError(t({ id: 'toasts.failedToCopy' }));
    } finally {
      setLoading(false);
    }
  };

  const onCopyNumber = async () => {
    try {
      setLoading(true);

      const { data: numberAndCvn } = await dispatch(
        cardApi.endpoints.getNumberAndCvn.initiate({
          accountId,
          cardId,
        })
      );
      const number = numberAndCvn
        ? numberAndCvn.response.number
        : undefined;
      if (!number) throw new Error();

      copyToClipboard(String(number));
      showSuccess(t({ id: 'toasts.copied' }));
    } catch (err) {
      showError(t({ id: 'toasts.failedToCopy' }));
    } finally {
      setLoading(false);
    }
  };

  const onCopyDate = async () => {
    try {
      setLoading(true);

      const { data: info } = await dispatch(
        cardApi.endpoints.getCardInfo.initiate({
          accountId,
          cardId,
        })
      );
      const expiration_month =
        info?.response.expirationDate?.slice(5, 7) || undefined;
      const expiration_year =
        info?.response.expirationDate?.slice(2, 4) || undefined;
      if (!expiration_month || !expiration_year) throw new Error();

      const monthParsed =
        expiration_month.length === 1
          ? '0' + expiration_month
          : expiration_month;
      copyToClipboard(monthParsed + '/' + expiration_year);
      showSuccess(t({ id: 'toasts.copied' }));
    } catch (err) {
      showError(t({ id: 'toasts.failedToCopy' }));
    } finally {
      setLoading(false);
    }
  };

  return (
    <div style={{ position: 'relative', width: '100%', marginTop: 32 }}>
      {loading && (
        <div style={{
          position: 'absolute',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          backgroundColor: 'rgba(255, 255, 255, 0.8)',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          zIndex: 10,
          borderRadius: 8
        }}>
          <Spin size="large" />
        </div>
      )}

      <Flex justify="space-between" align="center" style={{ marginBottom: 16 }}>
        <Title level={5} style={{ margin: 0 }}>{t({ id: 'card.requisites' })}</Title>
        <RoundContainer onClick={onToggle} size={32} pointer color="rgba(0, 0, 0, 0.08)">
          {shown ? <EyeInvisibleFilled /> : <EyeFilled />}
        </RoundContainer>
      </Flex>

      <Space direction="vertical" size={8} style={{ width: '100%' }}>
        <Flex justify="space-between" align="center" style={{ width: '100%', backgroundColor: 'rgba(0, 0, 0, 0.08)', padding: 12, borderRadius: 16 }}>
          {shown ? (
            <Space size={6}>
              <Text>{String(dataToShow.number).slice(0, 4)}</Text>
              <Text>{String(dataToShow.number).slice(4, 8)}</Text>
              <Text>
                {String(dataToShow.number).slice(8, 12)}
              </Text>
              <Text>{String(dataToShow.number).slice(12)}</Text>
            </Space>
          ) : (
            <Space size={12}>
              <Text>••••</Text>
              <Text>••••</Text>
              <Text>••••</Text>
              <Text>{numberMasked.slice(-4)}</Text>
            </Space>
          )}
          <Button
            type="text"
            icon={<CopyOutlined />}
            onClick={onCopyNumber}
            size="small"
          />
        </Flex>

        <Flex gap={8}>
          <Flex justify="space-between" align="center" style={{ width: '100%', backgroundColor: 'rgba(0, 0, 0, 0.08)', padding: 12, borderRadius: 16 }}>
            {shown ? (
              <Text>{dataToShow.expirationDate}</Text>
            ) : (
              <Space size={6}>
                <Text>••</Text>
                <Text>/</Text>
                <Text>••</Text>
              </Space>
            )}
            <Button
              type="text"
              icon={<CopyOutlined />}
              onClick={onCopyDate}
              size="small"
            />
          </Flex>
          <Flex justify="space-between" align="center" style={{ width: '100%', backgroundColor: 'rgba(0, 0, 0, 0.08)', padding: 12, borderRadius: 16 }}>
            {shown ? (
              <Text>{dataToShow.cvn}</Text>
            ) : (
              <Text>•••</Text>
            )}
            <Button
              type="text"
              icon={<CopyOutlined />}
              onClick={onCopyCvn}
              size="small"
            />
          </Flex>
        </Flex>
      </Space>
    </div>
  );
};

export default Requisites;